import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { fetchTemporaryBadgeStatus, fetchUserPicture } from '../../api/mobileapi';
import { HomeData } from '../../api/types';
import { AdamsError, getErrorMessage } from '../../utils/errors';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { UserSiteAccessInfo } from '../UserSiteAccessInfo/UserSiteAccessInfo';

interface TemporaryBadgeScanResultProps {
    nonce: string | undefined;
    translations: Record<string, string>;
}

export const TemporaryBadgeScanResult: React.FC<TemporaryBadgeScanResultProps> = ({ nonce, translations }) => {
    const { currentLanguage } = useHomeData();
    const [redirectToTemporaryBadgeScan, setRedirectToTemporaryBadgeScan] = useState(false);

    const {
        data,
        error: badgeError,
        isLoading: badgeLoading,
    } = useQuery<HomeData, AdamsError>(
        ['temporaryBadgeStatus', nonce],
        () => fetchTemporaryBadgeStatus({ data: { nonce }, language: currentLanguage }),
        {
            enabled: !!nonce,
        }
    );

    const {
        data: scannedUserPicture,
        error: pictureError,
        isLoading: pictureLoading,
    } = useQuery<string | undefined, AdamsError>(['userPicture', data], () => fetchUserPicture(data!), {
        enabled: !!data,
    });

    const loading = badgeLoading || pictureLoading;
    const errorOccured = badgeError || pictureError;
    const errorMessage = getErrorMessage(badgeError) || getErrorMessage(pictureError) || '';

    const reset = () => setRedirectToTemporaryBadgeScan(true);

    if (redirectToTemporaryBadgeScan) {
        return <Navigate to="/functions/temporary-badge" />;
    }

    return (
        <Card>
            <CardContent>
                {errorOccured && (
                    <ErrorMessage
                        errorMessage={errorMessage}
                        title={translations.ERROR_NOT_ACP_QR_CODE_TITLE}
                        translations={translations}
                    />
                )}
                {loading && !errorOccured && <LinearProgress />}
                {data && scannedUserPicture && <UserSiteAccessInfo data={data} userPicture={scannedUserPicture} />}
                {(errorOccured || (data && scannedUserPicture)) && !loading && (
                    <CardActions style={{ display: 'none' }}>
                        <Button fullWidth color="primary" variant="contained" size="large" onClick={reset}>
                            {translations.TEMPORARY_BADGE_RESCAN_BUTTON}
                        </Button>
                    </CardActions>
                )}
            </CardContent>
        </Card>
    );
};
